import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useTranslation } from "react-i18next";

export default function ContactPage() {
  const { t } = useTranslation(["pages"]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setStatus("sending");
    try {
      const body = new URLSearchParams(formData as Record<string, string>);
      await fetch("https://readdy.ai/api/form/d772o0l5uau42b5in1ig", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  const subjectOptions = t("pages:contact.form.subjectOptions", { returnObjects: true }) as Array<{ value: string; label: string }>;

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative pt-32 pb-20 bg-slate-900">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/52bcf307e171967df9912d45a7d7a004.jpg"
            alt="Contact"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4">
            {t("pages:contact.hero.subtitle")}
          </p>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("pages:contact.hero.title")}
          </h1>
          <p className="text-slate-300 text-lg max-w-xl mx-auto">
            {t("pages:contact.hero.description")}
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Info */}
          <div className="space-y-8">
            <div>
              <p className="text-emerald-700 text-sm font-semibold uppercase tracking-widest mb-4">
                {t("pages:contact.info.subtitle")}
              </p>
              <h2
                className="text-2xl font-bold text-slate-900 mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("pages:contact.info.title")}
              </h2>
              <p className="text-slate-500 text-sm leading-relaxed">
                {t("pages:contact.info.description")}
              </p>
            </div>
            {[
              {
                icon: "ri-map-pin-line",
                label: t("pages:contact.info.items.address"),
                value: "1411 PALE SAN VITORES ROAD, STE 303,\nTAMUNING, GU",
                href: null,
              },
              {
                icon: "ri-phone-line",
                label: t("pages:contact.info.items.phone"),
                value: "+1.671.482.0104",
                href: "tel:+16714820104",
              },
              {
                icon: "ri-mail-line",
                label: t("pages:contact.info.items.email"),
                value: "info@medigreen.energy",
                href: "mailto:info@medigreen.energy",
              },
              {
                icon: "ri-time-line",
                label: t("pages:contact.info.items.hours"),
                value: t("pages:contact.info.hoursValue"),
                href: null,
              },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 flex-shrink-0">
                  <i className={`${item.icon} text-lg`} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1">
                    {item.label}
                  </p>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-slate-700 text-sm whitespace-pre-line hover:text-emerald-700 transition-colors cursor-pointer"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-slate-700 text-sm whitespace-pre-line">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-2 bg-slate-50 rounded-2xl p-8 md:p-10">
            {status === "done" ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 mb-4">
                  <i className="ri-check-line text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {t("pages:contact.form.success.title")}
                </h3>
                <p className="text-slate-500">{t("pages:contact.form.success.description")}</p>
              </div>
            ) : (
              <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="space-y-5">
                <h3
                  className="text-xl font-bold text-slate-900 mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {t("pages:contact.form.title")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
                      {t("pages:contact.form.name")}
                    </label>
                    <input
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t("pages:contact.form.namePlaceholder")}
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-emerald-500 bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
                      {t("pages:contact.form.email")}
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t("pages:contact.form.emailPlaceholder")}
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-emerald-500 bg-white transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
                    {t("pages:contact.form.company")}
                  </label>
                  <input
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t("pages:contact.form.companyPlaceholder")}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-emerald-500 bg-white transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
                    {t("pages:contact.form.subject")}
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-emerald-500 bg-white transition-colors cursor-pointer"
                  >
                    {subjectOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
                    {t("pages:contact.form.message")}{" "}
                    <span className="normal-case font-normal text-slate-400">
                      ({formData.message.length}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t("pages:contact.form.messagePlaceholder")}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-emerald-500 bg-white transition-colors resize-none"
                  />
                  {formData.message.length > 500 && (
                    <p className="text-red-500 text-xs mt-1">
                      {t("pages:contact.form.charError")}
                    </p>
                  )}
                </div>
                {status === "error" && (
                  <p className="text-red-500 text-sm">{t("pages:contact.form.error")}</p>
                )}
                <button
                  type="submit"
                  disabled={status === "sending" || formData.message.length > 500}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-3.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {status === "sending" ? t("pages:contact.form.sending") : t("pages:contact.form.submit")}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}